import { AlertTriangle, Loader2 } from "lucide-react";
import { useId, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { type AppSettings, useAppSettings, useUpdateAppSettings } from "@/hooks/use-app-settings";

/**
 * Settings → Config → Email testing.
 *
 * Diverts every email the app sends to one address, so a round can be opened —
 * or any other feature exercised — without mailing real people.
 *
 * The switch is applied at `sendEmail` (`src/lib/email/send.ts`), the single
 * function all mail in this project goes through, so it catches everything
 * including features added after it: review invitations and reminders, HR
 * change notices, error alerts.
 *
 * The risk this page creates is leaving it on, so the state is stated loudly
 * here and again on Settings → Logs → Email Outbox, where somebody wondering
 * why mail has not arrived will actually be looking.
 */
export function SettingsEmailTestingPage() {
  const { data, isLoading, isError, error, refetch } = useAppSettings();

  if (isError) {
    return (
      <ErrorState
        message={
          error instanceof Error
            ? `Couldn't load email testing: ${error.message}`
            : "Couldn't load email testing"
        }
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <p className="max-w-3xl text-muted-foreground text-sm">
        While this is on, every email Enable sends goes to one address instead of the people it is
        addressed to — review invitations and reminders, change notices, feedback and error alerts,
        all of it. Use it to try a feature out without mailing anybody at the company.
      </p>

      {/*
        The form only mounts once the settings are here, and is keyed on the
        saved address so a change made elsewhere remounts it. The first cut kept
        the field in sync with a `useEffect` while rendering a disabled input
        during load — and the effect fired when the data arrived, wiping
        whatever had been typed in the meantime.
      */}
      {isLoading || !data ? (
        <Skeleton className="h-52 w-full" />
      ) : (
        <EmailTestingForm key={data.testEmailAddress ?? ""} settings={data} />
      )}
    </div>
  );
}

function EmailTestingForm({ settings }: { settings: AppSettings }) {
  const update = useUpdateAppSettings();
  const addressId = useId();
  const switchId = useId();
  const on = settings.testEmailEnabled;
  const saved = settings.testEmailAddress ?? "";
  const [address, setAddress] = useState(saved);
  const trimmed = address.trim();
  const dirty = trimmed !== saved;
  const busy = update.isPending;

  return (
    <div className="space-y-6">
      {on && (
        <div className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning/10 p-4">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning-foreground" aria-hidden />
          <div className="text-sm">
            <p className="font-medium">No email is reaching anybody right now</p>
            <p className="text-muted-foreground">
              Everything Enable sends is going to <strong>{saved}</strong>. Turn this off when you
              have finished testing, or real notifications will keep being swallowed.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-4 rounded-lg border border-border p-4">
        <div className="space-y-2">
          <Label htmlFor={addressId}>Test email address</Label>
          <p className="max-w-3xl text-muted-foreground text-sm">
            Where the diverted mail goes. Each email says who it would have gone to, and its subject
            is prefixed <code className="text-xs">[TEST]</code>.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id={addressId}
              type="email"
              value={address}
              disabled={busy}
              placeholder="you@nymbl.app"
              className="max-w-sm"
              onChange={(e) => setAddress(e.target.value)}
            />
            {dirty && (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => update.mutate({ testEmailAddress: trimmed || null })}
              >
                Save address
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-start gap-4 border-border border-t pt-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-medium">Divert all email</p>
              <StatusPill
                tone={on ? "warning" : "neutral"}
                label={on ? "Diverting" : "Sending normally"}
              />
            </div>
            <p className="mt-1 text-muted-foreground text-sm">
              {/*
                Said here as well as refused by the server: an armed switch with
                nowhere to send would silently drop every email in the workspace.
              */}
              {saved
                ? `Nothing will reach anyone but ${saved}.`
                : "Save an address first — the switch can't be turned on without one."}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 pt-0.5">
            {busy && <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden />}
            <Label
              htmlFor={switchId}
              className={busy ? "text-muted-foreground" : "cursor-pointer text-muted-foreground"}
            >
              {on ? "On" : "Off"}
            </Label>
            <Checkbox
              id={switchId}
              checked={on}
              disabled={busy || (!saved && !on)}
              aria-label={`Turn email diversion ${on ? "off" : "on"}`}
              onCheckedChange={(next) => update.mutate({ testEmailEnabled: next === true })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
